import {
  SimulatorResult,
  GetApplicationsQueryResult,
  AttachmentResult,
} from '@sdir/httpclient/lib/clients/aps/application';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { downloadApplicationFile } from '../../../helpers/attachmentApiHelpers';
import { getApplicationApi, qualificationsApi } from '../../../httpclient';
import { CurrentApplicationContext } from '../../../services/contexts';
import { Loadable } from '../../../types';

export const CurrentApplicationProvider: React.FC<{ paramKey?: string; children: any }> = ({
  children,
  paramKey = 'applicationUid',
}) => {
  const applicationUid = useParams()[paramKey];
  const currentAppId = useRef<string>(applicationUid || '');
  /**
   * Løpenummeret til den ferskeste hentingen.
   *
   * Uid-en alene duger ikke som vaktpost: to stille oppfriskninger for SAMME
   * søknad kan være i lufta samtidig — søkeren krysser av for to erklæringer
   * rett etter hverandre — og uten et generasjonsnummer kan det eldste svaret
   * lande sist og skrive over det nyeste. Da ville den andre haken sett ubesvart
   * ut igjen.
   */
  const requestSeq = useRef(0);
  const attachmentCache = useRef<{ [blobId: string]: Promise<File> }>({});

  const [application, setApplication] = useState<Loadable<GetApplicationsQueryResult>>({});
  const [qualifications, setQualifications] = useState<Loadable<SimulatorResult>>({});

  /**
   * Henter søknaden på nytt.
   *
   * `keepResult` beholder forrige svar mens det nye hentes. Standardveien
   * nullstiller `result` og setter `loading`, og da bytter sidene til en loader
   * og avmonterer alt som er tegnet av søknaden — også ekspanderte kravgrupper
   * søkeren står midt i. Der oppfriskningen er en FØLGE av en handling hun gjør
   * inne i en slik flate, skal den gå stille: det som ligger blir stående til
   * det nye kommer, og flatene beholder tilstanden sin.
   *
   * Løftet gjelder også når hentingen FEILER. En stille oppfriskning skal aldri
   * rive bort det som står — sidene bytter til feilflate på `application.error`,
   * og da ville et hikk i nettet kastet søkeren ut av kravsiden hun nettopp
   * krysset av på, uten vei tilbake. Da blir forrige svar stående, og feilen
   * logges: handlingen hennes gikk gjennom, det er bare oppfriskningen som ikke
   * kom fram, og neste handling henter uansett på nytt.
   *
   * Uten flagget er oppførselen den samme som før — legacy-sidene deler denne
   * provideren, og skal ikke merke noe.
   */
  const loadCurrentApplication = useCallback((keepResult = false) => {
    const idToLoad = currentAppId.current;
    requestSeq.current += 1;
    const seq = requestSeq.current;
    /** Er dette fortsatt den hentingen flatene venter på? */
    const isCurrent = () => idToLoad === currentAppId.current && seq === requestSeq.current;

    if (!keepResult) setApplication({ loading: true });
    getApplicationApi
      .v2GetGetapplicationbyuidv2UidGet(idToLoad)
      .then((r) => {
        if (isCurrent()) {
          setApplication({ result: r.data });
        }
      })
      .catch((e) => {
        if (!isCurrent()) return;
        setApplication((previous) => {
          if (keepResult && previous.result) {
            // eslint-disable-next-line no-console
            console.error('Stille oppfriskning av søknaden feilet — forrige svar blir stående', e);
            return previous;
          }
          return { error: e };
        });
      });
  }, []);

  const resetQualifications = useCallback(() => {
    setQualifications({});
  }, []);

  const loadQualifications = useCallback(() => {
    if (!application.result?.applicationType) return;
    const idToLoad = currentAppId.current;
    setQualifications({ loading: true });
    qualificationsApi
      .v1QualificationsGetqualificationsforapplicationApplicationUidPost(idToLoad)
      .then((r) => {
        if (idToLoad === currentAppId.current) {
          setQualifications({ result: r.data });
        }
      })
      .catch((e) => {
        if (idToLoad === currentAppId.current) {
          setQualifications({ error: e });
        }
      });
  }, [application]);

  const getAttachment = useCallback((attachment: AttachmentResult): Promise<File> => {
    if (!currentAppId.current || !attachment.uid)
      throw new Error('Invalid attachment or application ID');

    const attachmentUid = attachment.uid;

    const cachedPromise = attachmentCache.current[attachmentUid];
    if (cachedPromise) return cachedPromise;
    const appIdToLoadFor = currentAppId.current;

    const newLoadPromise = downloadApplicationFile(appIdToLoadFor, attachment);

    attachmentCache.current[attachmentUid] = newLoadPromise;

    newLoadPromise.catch(() => {
      if (appIdToLoadFor === currentAppId.current) {
        delete attachmentCache.current[attachmentUid];
      }
    });

    return newLoadPromise;
  }, []);

  useEffect(() => {
    currentAppId.current = applicationUid || '';
    loadCurrentApplication();
    resetQualifications();
  }, [applicationUid, loadCurrentApplication, resetQualifications]);

  const value = {
    application,
    refresh: loadCurrentApplication,
    qualifications,
    loadQualifications,
    getAttachment,
  };

  return (
    <CurrentApplicationContext.Provider value={value}>
      {children}
    </CurrentApplicationContext.Provider>
  );
};
